// ============================================================================
//  NhisCatalogueImport.js — load the NHIS tariff spreadsheets into the catalogue
// ----------------------------------------------------------------------------
//  NHIS issues its catalogue as spreadsheets. This screen turns them into the
//  tables the billing and claims screens actually read from.
//
//  Four sections, because there are four sheets in a release and they land in
//  four different tables. Services and Diagnoses are built; Procedures and
//  Medications are shown rather than hidden so the shape of the screen is honest
//  about what is coming, and each says which table it will write to.
//
//  ONE SECTION COMPONENT, ONE CONFIG PER SHEET
//
//  SheetSection below is driven by SHEET_KINDS: the endpoints, the table columns
//  and the wording all come from a config entry. Services and Diagnoses differ in
//  what a row IS — one has a tariff, the other an ICD-to-G-DRG pairing — but the
//  flow around them is identical, and duplicating three hundred lines per sheet
//  type is how the fourth one ends up subtly different from the first.
//
//  THE ORDER OF OPERATIONS IS THE POINT
//
//  Pick a file → the browser reads it → the SERVER says what would change →
//  the operator confirms → the server writes. Re-pricing 437 live services is
//  not something to discover after the fact, so the preview is not skippable:
//  Commit stays disabled until a preview has been run against the actual table.
// ============================================================================

import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, TextField, IconButton, Collapse, Chip,
  CircularProgress, Tooltip, MenuItem, ToggleButtonGroup, ToggleButton,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  ExpandMore as ExpandIcon,
  CheckCircle as OkIcon,
  AddCircle as NewIcon,
  SwapHoriz as ChangeIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
  Remove as SameIcon,
  MedicalServices as ServiceIcon,
  Coronavirus as DiagnosisIcon,
  Vaccines as MedicineIcon,
  ContentCut as ProcedureIcon,
  //  Labs and imaging. A flask rather than a microscope: the section covers both
  //  bench tests and scans, and a microscope would claim only the first.
  Science as InvestigationIcon,
  Lock as LockIcon,
  PlayArrow as PreviewIcon,
  Save as CommitIcon,
  Inventory2 as HistoryIcon,
  InfoOutlined as InfoIcon,
  CallSplit as ForkIcon,
  Block as SkipIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';

import { BRAND, brandFieldSx, brandButtonSx, brandGhostButtonSx, FONT_STACK } from '../../brand';
import { nhisCatalogueImportUrls } from '../BaseURL';
import { parseWorkbook, downloadTemplate, preferredSheetIndex } from './nhisSheet';
import notify from './notify';

const CARD = {
  radius: '14px',
  shadow: '0 1px 2px rgba(15,31,61,.04), 0 8px 24px rgba(15,31,61,.05)',
};

// How each outcome is presented. Every one carries an ICON as well as a colour:
// the palette's red and green sit ~2 ΔE apart under deuteranopia, so a status
// told in colour alone is a status a red-green colourblind operator cannot read.
const ACTION_META = {
  insert:    { label: 'Add',       icon: NewIcon,    color: BRAND.green,  soft: BRAND.greenSoft },
  update:    { label: 'Update',    icon: ChangeIcon, color: BRAND.info,   soft: '#E7F1FA' },
  unchanged: { label: 'No change', icon: SameIcon,   color: BRAND.inkFaint, soft: BRAND.canvas },
  blocked:   { label: 'Problem',   icon: ErrorIcon,  color: BRAND.danger, soft: '#FBEAEA' },
  // Amber, not red: nothing is wrong with the row. The sheet wants to bill a
  // diagnosis on a G-DRG the catalogue does not hold for it, and only a person
  // can say whether that is the missing half of an age pair or a mistake.
  conflict:  { label: 'Your call',  icon: ForkIcon,  color: '#B4690E',      soft: '#FDF3E3' },
  declined:  { label: 'Left out',   icon: SkipIcon,  color: BRAND.inkFaint, soft: BRAND.canvas },
};

// The three shapes a second G-DRG takes, worst first — the order the review lists
// them in. `blurb` is what the operator is actually being asked.
const CONFLICT_KINDS = [
  {
    key: 'cross-specialty',
    label: 'Crosses a specialty',
    color: '#B4690E',
    blurb: 'A different specialty, and not an age split. Typhoid fever held under '
      + 'Medical as "Typhoid perforation" and arriving as a laparotomy under Adult '
      + 'Surgery is this shape. Worth reading each one.',
  },
  {
    key: 'same-specialty',
    label: 'Another G-DRG in the same specialty',
    color: BRAND.info,
    blurb: 'Usually two ways of treating one condition. "Amoebic liver abscess" is '
      + 'drained percutaneously under ASUR14A or by laparotomy under ASUR16A, and '
      + 'both are real tariffs.',
  },
  {
    key: 'age-band',
    label: 'The adult / child pair',
    color: BRAND.green,
    blurb: 'The catalogue holds one age band and the sheet brings the other. These '
      + 'never compete — a claim for a child cannot reach the adult G-DRG — so '
      + 'keeping both is nearly always right.',
  },
];

//  ── THE TWO COMMIT MODES, PER KIND ─────────────────────────────────────────
//  'upsert' is always the first and always means the same thing. The second differs:
//  four kinds offer "add new only"; investigations offers "codes only", which is its
//  reverse — touch what is already there and add nothing.
//
//  A function of the config rather than a field on it, so a kind that says nothing gets
//  the pair four of them share and only the exception has to declare itself.
const MODES = (cfg) => [
  ['upsert', cfg.upsertLabel || 'Refresh & add new'],
  [cfg.altMode || 'insert-only', cfg.altModeLabel || 'Add new only'],
];

// ── What differs between sheet types ────────────────────────────────────────
//  `columns` describes the preview table. `cell` pulls the display value off a
//  planned row; `sub` renders the second line under it (a former tariff, a former
//  G-DRG) when the row is an update.
const SHEET_KINDS = {
  services: {
    n: 1,
    title: 'Services',
    icon: ServiceIcon,
    table: 'insured_services',
    item: 'service',
    dropHint: '.xlsx or .xls — G-DRG code, service name and tariff.',
    existingLabel: 'Existing services',
    upsertHint:
      'Tariff, name, age band and specialty are refreshed on services already in the '
      + 'catalogue. Top-up and charge type are left exactly as you set them.',
    insertOnlyHint:
      'Services already in the catalogue are not touched at all. Only codes that do '
      + 'not exist yet are added.',
    columns: [
      { key: 'rowNumber', label: 'Row', width: 52, faint: true, mono: true },
      { key: 'code', label: 'G-DRG', width: 92, mono: true, bold: true },
      { key: 'service', label: 'Service', primary: true },
      { key: 'age', label: 'Age', width: 72, tone: 'age' },
      { key: 'amo', label: 'Tariff', width: 130, money: true, changeOf: 'amo' },
    ],
  },

  //  ── INVESTIGATIONS ────────────────────────────────────────────────────────
  //  Second, right after Services, because that is the order a claim is built and the
  //  order the sheets arrive in.
  //
  //  THE ONLY KIND HERE THAT NEVER WRITES A PRICE. Its sheet has three columns and no
  //  tariff — that is what the NHIA publishes — so the two modes are not "refresh
  //  everything" and "add only". They are "fill in codes and add what is missing" and
  //  "fill in codes and add nothing", because an added test is offered on a claim
  //  immediately, at no price.
  investigations: {
    n: 2,
    title: 'Investigations',
    icon: InvestigationIcon,
    //  Two tables, which no other kind has. Named here as one string because this is
    //  the caption under the title, and "lab_prices + scan_price" is the honest answer
    //  to "where does this land".
    table: 'lab_prices + scan_price',
    item: 'investigation',
    //  The exception MODES() exists for: the second mode here is the reverse of the
    //  other four kinds', so it declares itself rather than inheriting the wrong word.
    altMode: 'codes-only',
    altModeLabel: 'Codes only',
    upsertLabel: 'Fill codes & add new',
    dropHint: '.xlsx or .xls — G-DRG code and investigation name. There is no price '
      + 'column on this sheet and none is written.',
    existingLabel: 'Existing investigations',
    upsertHint:
      'The G-DRG and the name are filled in on tests you already have — matched by name '
      + 'where the code is missing, which is what makes a test billable at all. Tests '
      + 'you do not have are added WITH NO PRICE, and are offered on claims at nothing '
      + 'until you price them. Your prices and top-ups are never touched.',
    insertOnlyHint:
      'Only tests you already have are touched, filling in the G-DRG and the name. '
      + 'Nothing is added, so nothing arrives unpriced.',
    columns: [
      { key: 'rowNumber', label: 'Row', width: 52, faint: true, mono: true },
      { key: 'code', label: 'G-DRG', width: 92, mono: true, bold: true },
      { key: 'name', label: 'Investigation', primary: true },
      //  Which of the two tables it lands in. Shown per row because the routing is by
      //  NAME — an operator is the one who knows whether their facility files a Doppler
      //  under scans, and this is where they can see the call being made.
      { key: 'targetLabel', label: 'Goes to', width: 84 },
    ],
  },

  diagnoses: {
    n: 3,
    title: 'Diagnoses',
    icon: DiagnosisIcon,
    table: 'diagnosis_tarf',
    item: 'diagnosis',
    dropHint: '.xlsx or .xls — either the NHIS release layout (MDC category, Core '
      + 'G-DRG, ICD-10 code, diagnosis, level) or the ICD-matched one (ICD-10 category, '
      + 'ICD-10 code, diagnosis, G-DRG code, NHIA specialty, G-DRG description).',
    existingLabel: 'Existing diagnoses',
    upsertHint:
      'Name, age band, category, specialty and level are refreshed on diagnoses already '
      + 'in the catalogue, matched on ICD-10 code plus G-DRG. The tariff is written only '
      + 'if your sheet has a price column — without one, prices already set are left '
      + 'exactly as they are. Top-up is never touched. A diagnosis arriving under a '
      + 'G-DRG the catalogue does not hold for it is not written until you say whether '
      + 'to keep both.',
    insertOnlyHint:
      'Diagnoses already in the catalogue are not touched at all. Only ICD-10 and G-DRG '
      + 'pairs that do not exist yet are added.',
    columns: [
      { key: 'rowNumber', label: 'Row', width: 52, faint: true, mono: true },
      { key: 'icd', label: 'ICD-10', width: 88, mono: true, bold: true },
      { key: 'core', label: 'Core G-DRG', width: 108, mono: true, changeOf: 'core' },
      { key: 'name', label: 'Diagnosis', primary: true },
      { key: 'cat', label: 'MDC', width: 100 },
      { key: 'age_cat', label: 'Age', width: 72, tone: 'age' },
      // Only the ICD-matched layout has this, and it is where the age band comes
      // from on that layout — so an operator who wonders why "Cholera" is Adult
      // can see the answer on the same line instead of taking it on trust.
      { key: 'gdrgName', label: 'G-DRG is for', width: 190 },
      { key: 'level', label: 'Level', width: 74, mono: true },
      // Shown whether or not the sheet supplied one, so "0.00" on every row is a
      // visible statement that this sheet carries no prices — rather than the
      // column being absent and the question going unanswered.
      { key: 'tariff', label: 'Tariff', width: 120, money: true, changeOf: 'tariff' },
    ],
  },
};

SHEET_KINDS.procedures = {
  n: 4,
  title: 'Procedures',
  icon: ProcedureIcon,
  table: 'procedure_tarf',
  item: 'procedure',
  dropHint: '.xlsx or .xls — MDC category, Core G-DRG, procedure name, and its ICD-10 indications.',
  existingLabel: 'Existing procedures',
  upsertHint:
    'Name, group, age band and specialty are refreshed on procedures already in the '
    + 'catalogue, matched on G-DRG plus procedure name. Tariff and top-up are never '
    + 'touched — they are set per facility on Procedure Setup.',
  insertOnlyHint:
    'Procedures already in the catalogue are not touched at all. Only G-DRG and '
    + 'procedure-name pairs that do not exist yet are added.',
  columns: [
    { key: 'rowNumber', label: 'Row', width: 52, faint: true, mono: true },
    { key: 'G_DRG', label: 'Core G-DRG', width: 100, mono: true, bold: true },
    { key: 'procedure_', label: 'Procedure', primary: true },
    { key: 'indicationCount', label: 'Indications', width: 96, tone: 'count' },
    { key: 'spe', label: 'MDC', width: 130 },
    { key: 'age', label: 'Age', width: 72, tone: 'age' },
    { key: 'level', label: 'Level', width: 74, mono: true },
  ],
};

SHEET_KINDS.medications = {
  n: 5,
  title: 'Medications',
  icon: MedicineIcon,
  table: 'store_pro',
  item: 'medicine',
  dropHint: '.xlsx or .xls — code, generic name with strength, unit of pricing, price and prescribing level.',
  existingLabel: 'Existing medicines',
  upsertHint:
    'Name, unit of pricing, price, dose unit and prescribing level are refreshed on '
    + 'medicines already in the catalogue, matched on code. Reorder level and top-up are '
    + 'never touched — those are yours to set on the Stores screen.',
  insertOnlyHint:
    'Medicines already in the catalogue are not touched at all. Only codes that do not '
    + 'exist yet are added, with a reorder level of 100 and no top-up.',
  columns: [
    { key: 'rowNumber', label: 'Row', width: 52, faint: true, mono: true },
    { key: 'code', label: 'Code', width: 100, mono: true, bold: true },
    { key: 'name', label: 'Generic name, form, strength', primary: true },
    { key: 'Dose_Unit', label: 'Dose unit', width: 116, mono: true, changeOf: 'Dose_Unit' },
    { key: 'pricing', label: 'Priced per', width: 96 },
    { key: 'cost', label: 'Price', width: 120, money: true, changeOf: 'cost' },
    { key: 'level', label: 'Rx level', width: 78, tone: 'age' },
  ],
};

// Nothing left unbuilt — all four sheets in a release now have a section. Kept as
// an empty list rather than deleted: the next thing NHIS publishes goes here.
const PLANNED = [];

// This platform has no server-side actor on these routes (see the note in
// routes/ictClaimsImport.js), so the operator name comes from the session the
// way every other screen here reads it. Wrapped because a corrupt userInfo blob
// must not take down an import.
function currentUserName() {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.name || u?.username || null;
  } catch {
    return null;
  }
}

// 'YYYY-MM-DD HH:mm:ss' from MySQL (db.js sets dateStrings). Safari refuses the
// space form, so it is replaced before parsing rather than left to chance.
function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function NhisCatalogueImport() {
  // Keyed by sheet kind: committing a diagnosis sheet must refresh the diagnosis
  // record and leave the services one alone.
  const [historyKeys, setHistoryKeys] = useState(
    () => Object.fromEntries(Object.keys(SHEET_KINDS).map((k) => [k, 0]))
  );
  const bump = (kind) => setHistoryKeys((k) => ({ ...k, [kind]: (k[kind] || 0) + 1 }));

  return (
    <Box sx={{ fontFamily: FONT_STACK, pb: 4 }}>
      <PageHead />
      {/* Driven off the config so a fifth sheet type is one entry, not another
          hand-written section. Ordered by its number, the way a release is. */}
      {Object.keys(SHEET_KINDS)
        .sort((a, b) => SHEET_KINDS[a].n - SHEET_KINDS[b].n)
        .map((kind) => (
          <SheetSection key={kind} kind={kind} onImported={() => bump(kind)} />
        ))}
      {PLANNED.map((p) => <PlannedSection key={p.n} {...p} />)}
      <ImportRecordSection refreshTokens={historyKeys} />
    </Box>
  );
}

// ── Page head ───────────────────────────────────────────────────────────────
function PageHead() {
  return (
    <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
      <Typography sx={{
        fontSize: { xs: 20, sm: 23 }, fontWeight: 800, color: BRAND.ink,
        letterSpacing: '-.3px', lineHeight: 1.25,
      }}>
        NHIS Catalogue Import
      </Typography>
      <Typography sx={{
        fontSize: 13.5, color: BRAND.inkMuted, mt: .5, maxWidth: '78ch', lineHeight: 1.65,
      }}>
        Load an NHIS tariff spreadsheet into the catalogue the system bills from.
        Every import is previewed against the live table first — you see each price
        that would change before anything is written.
      </Typography>
    </Box>
  );
}

// ── Shared card shell ───────────────────────────────────────────────────────
function SectionCard({ n, title, icon: Icon, table, subtitle, right, disabled, children, open, onToggle }) {
  return (
    <Box sx={{
      background: BRAND.surface,
      border: `1px solid ${BRAND.line}`,
      borderRadius: CARD.radius,
      boxShadow: disabled ? 'none' : CARD.shadow,
      mb: 2,
      // No overflow:hidden. It would clip nothing that matters here, but it makes
      // this box a scroll container — and the sticky table header inside would
      // then attach to THIS box rather than the viewport, so it would never
      // actually stick. The corners are handled on the header below instead.
      opacity: disabled ? .72 : 1,
    }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: { xs: 1.75, sm: 2.5 }, py: 1.75,
          cursor: onToggle ? 'pointer' : 'default',
          userSelect: 'none',
          borderBottom: open ? `1px solid ${BRAND.line}` : 'none',
          // Rounded to match the card, because the card can no longer clip to
          // its own radius — without this the hover fill squares off the top.
          borderRadius: open ? `${CARD.radius} ${CARD.radius} 0 0` : CARD.radius,
          '&:hover': onToggle ? { background: BRAND.canvas } : {},
        }}
      >
        <Box sx={{
          width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: disabled ? BRAND.canvas : BRAND.navySoft,
          color: disabled ? BRAND.inkFaint : BRAND.navy,
        }}>
          <Icon sx={{ fontSize: 19 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: .875, flexWrap: 'wrap' }}>
            {/* Numbered only for the four sheet types. The record card is not a
                fifth sheet to import, so numbering it would imply it is. */}
            {n != null && (
              <Typography sx={{
                fontSize: 10.5, fontWeight: 800, color: BRAND.inkFaint, letterSpacing: '.6px',
              }}>
                {String(n).padStart(2, '0')}
              </Typography>
            )}
            <Typography sx={{ fontSize: 15.5, fontWeight: 750, color: BRAND.ink, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {/* The destination table, for the four sheet sections. The record
                card has no destination — it IS the record — so it passes none. */}
            {table && (
              <Typography sx={{ fontSize: 12, color: BRAND.inkFaint }}>
                → {table}
              </Typography>
            )}
          </Box>
          {subtitle && (
            <Typography sx={{ fontSize: 12, color: BRAND.inkMuted, mt: .125 }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>

        {right}
        {onToggle && (
          <ExpandIcon sx={{
            fontSize: 21, color: BRAND.inkFaint, flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .2s ease',
          }} />
        )}
      </Box>

      {/* Collapse's root is overflow:hidden, which would make it the sticky table
          header's scrollport and stop the header sticking. Checked rather than
          assumed: MUI's `entered` variant restores overflow:visible, so the
          header works whenever the card is fully open, and is only unstuck
          during the fold animation itself. */}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Not built yet ───────────────────────────────────────────────────────────
function PlannedSection({ n, title, icon, table, note }) {
  const [open, setOpen] = useState(false);
  return (
    <SectionCard
      n={n} title={title} icon={icon} table={table} disabled
      open={open} onToggle={() => setOpen((v) => !v)}
      right={
        <Chip
          size="small" icon={<LockIcon sx={{ fontSize: 14 }} />} label="Not built yet"
          sx={{
            height: 24, fontSize: 11, fontWeight: 700,
            background: BRAND.canvas, color: BRAND.inkMuted,
            border: `1px solid ${BRAND.line}`,
            '& .MuiChip-icon': { color: BRAND.inkFaint },
          }}
        />
      }
    >
      <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, lineHeight: 1.7, maxWidth: '70ch' }}>
        {note}
        {' '}This section needs one real sheet to confirm which spreadsheet column
        maps to which database column. A mapping guessed from the headings alone
        would import cleanly and put the wrong values in the wrong fields, which
        is harder to find later than a section that is plainly not finished.
      </Typography>
    </SectionCard>
  );
}

// ── One sheet type ──────────────────────────────────────────────────────────
function SheetSection({ kind, onImported }) {
  const cfg = SHEET_KINDS[kind];
  const api = useMemo(() => nhisCatalogueImportUrls(kind), [kind]);

  // Open by default — these are the sections you came for. Foldable because once
  // a sheet is loaded the card runs to the height of the whole catalogue, and
  // getting past it to anything else meant scrolling the entire list.
  const [open, setOpen] = useState(true);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);        // { fileName, sheets[] }
  const [sheetIdx, setSheetIdx] = useState(0);
  const [parsing, setParsing] = useState(false);
  const [templating, setTemplating] = useState(false);

  const [releaseLabel, setReleaseLabel] = useState('');
  const [labelError, setLabelError] = useState('');
  const [mode, setMode] = useState('upsert');

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [rowFilter, setRowFilter] = useState('all');
  const [dragging, setDragging] = useState(false);

  // The operator's answers to the second-G-DRG review, keyed by ICD-10:
  // { 'A00': 'keep', 'A01.0': 'skip' }. An ICD-10 that is absent is UNDECIDED,
  // which is not the same as 'skip' — the server refuses to write either, and
  // reports them apart.
  const [decisions, setDecisions] = useState({});

  const inputRef = useRef(null);
  const sheet = parsed?.sheets?.[sheetIdx] || null;

  // Anything that changes WHAT would be written invalidates a preview that was
  // run against the old input. Leaving a stale preview on screen next to an
  // enabled Commit button is how the wrong sheet gets imported.
  //
  // The decisions go with it. They are answers to questions asked about a
  // specific catalogue state, and carrying them across a re-check would apply
  // "keep both" to a comparison the operator never saw.
  const invalidate = () => { setPreview(null); setCommitted(null); setDecisions({}); };

  const reset = () => {
    setFile(null); setParsed(null); setSheetIdx(0);
    invalidate(); setRowFilter('all');
    if (inputRef.current) inputRef.current.value = '';
  };

  // Takes its rows as an argument rather than reading `sheet` off state, because
  // the two callers that matter fire in the same tick as the setState that would
  // have produced them — picking a file, and switching sheet.
  const runPreview = useCallback(async (rowsToCheck) => {
    if (!rowsToCheck?.length) return;
    setPreviewing(true);
    setPreview(null);
    setCommitted(null);
    setDecisions({});
    try {
      const { data } = await axios.post(api.preview, { rows: rowsToCheck });
      setPreview(data);
      setRowFilter(data.counts.blocked > 0 ? 'problems' : 'changes');
    } catch (err) {
      notify.error('Could not check the catalogue', err?.response?.data?.error || err.message);
    } finally {
      setPreviewing(false);
    }
  }, [api]);

  const ingest = useCallback(async (picked) => {
    if (!picked) return;
    if (!/\.xlsx?$/i.test(picked.name)) {
      notify.error('Not a spreadsheet', 'Pick an .xlsx or .xls file.');
      return;
    }
    setParsing(true);
    setFile(picked);
    setPreview(null);
    setCommitted(null);
    try {
      const wb = await parseWorkbook(picked, kind);
      setParsed(wb);
      // Not simply the first tab: release workbooks open on a cover page, and
      // the template's own Example tab must never be the default.
      const idx = preferredSheetIndex(wb.sheets);
      setSheetIdx(idx);
      // Straight into the check. Picking a file IS the request to see what is in
      // it, and making that a second button press meant staring at a file name
      // and a row count — the two things you already knew — until you pressed it.
      // Reading is free: the preview endpoint only SELECTs, and Commit is still
      // the only thing that writes.
      await runPreview(wb.sheets[idx]?.rows || []);
    } catch (err) {
      notify.error('Could not read that file', err.message || 'Unknown error');
      setFile(null);
      setParsed(null);
    } finally {
      setParsing(false);
    }
  }, [runPreview, kind]);

  const pickSheet = (i) => {
    setSheetIdx(i);
    setCommitted(null);
    const rows = parsed?.sheets?.[i]?.rows || [];
    if (rows.length) runPreview(rows);
    else setPreview(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    ingest(e.dataTransfer?.files?.[0]);
  };

  const getTemplate = async () => {
    setTemplating(true);
    try {
      await downloadTemplate(kind);
    } catch (err) {
      notify.error('Template failed', err.message || 'Unknown error');
    } finally {
      setTemplating(false);
    }
  };

  // The manual re-check. Still needed: the catalogue can move under you while
  // this screen is open — someone editing a tariff on Services Setup, or your own
  // commit a moment ago — and after a commit the figures on screen describe the
  // table as it WAS.
  const doPreview = () => runPreview(sheet?.rows || []);

  // The diagnoses the sheet and the catalogue disagree about, and how many are
  // still unanswered. Both drive the Commit button: an outstanding question means
  // rows the server will refuse to write, and a Commit that quietly leaves 690
  // rows out is worse than one that will not fire yet.
  // Memoised, not `preview?.conflictGroups || []`. That expression mints a fresh
  // array on every render whenever there are no conflicts, so the two useMemos
  // below would recompute every render and never actually memoise — the same
  // fresh-object-in-a-dependency-list mistake this codebase has fixed before.
  const conflictGroups = useMemo(
    () => preview?.conflictGroups || [],
    [preview]
  );
  const undecidedGroups = useMemo(
    () => conflictGroups.filter((g) => !decisions[g.icd]),
    [conflictGroups, decisions]
  );
  const keptRows = useMemo(
    () => conflictGroups
      .filter((g) => decisions[g.icd] === 'keep')
      .reduce((n, g) => n + g.proposed.length, 0),
    [conflictGroups, decisions]
  );

  const decide = (icd, verdict) => setDecisions((d) => {
    const next = { ...d };
    if (verdict === null) delete next[icd]; else next[icd] = verdict;
    return next;
  });
  const decideMany = (icds, verdict) => setDecisions((d) => {
    const next = { ...d };
    for (const icd of icds) {
      if (verdict === null) delete next[icd]; else next[icd] = verdict;
    }
    return next;
  });

  const doCommit = async () => {
    if (!preview || !sheet) return;

    // Checked here as well as server-side. The endpoint is the rule, but finding
    // out via a red toast after pressing Commit on a 437-row import is a worse
    // way to learn it than a message on the field itself.
    const label = releaseLabel.trim();
    if (!label) {
      setLabelError('Give this release a name so the import can be found again later');
      return;
    }

    // The button is already disabled for this, so reaching here means the state
    // moved between render and click. The server refuses undecided rows anyway;
    // this is so the operator is told rather than silently getting a short import.
    if (undecidedGroups.length) {
      notify.error(
        'Some diagnoses are still waiting on you',
        `${undecidedGroups.length} ${undecidedGroups.length === 1 ? 'diagnosis' : 'diagnoses'} `
        + 'arrive under a G-DRG the catalogue does not hold. Say whether to keep both, '
        + 'or leave them out, and Commit again.'
      );
      return;
    }

    setCommitting(true);
    try {
      const { data } = await axios.post(api.commit, {
        rows: sheet.rows,
        mode,
        releaseLabel: label,
        // Provenance for the record. Which workbook, and which tab of it — a
        // release ships out-patient and in-patient tariffs on separate sheets,
        // so the file name alone does not say what was loaded.
        fileName: file?.name || null,
        sheetName: sheet.name || null,
        importedBy: currentUserName(),
        // Only the diagnoses sheet raises these. Sent as {} otherwise, which the
        // server reads as "no decisions", and no other sheet produces a row that
        // needs one.
        conflictDecisions: decisions,
      });
      setCommitted(data.summary);
      onImported?.();
      const {
        inserted = 0, updated = 0, skipped = 0,
        conflictsKept = 0, conflictsDeclined = 0, conflictsUndecided = 0,
      } = data.summary || {};
      // The review's outcome belongs in the confirmation, not only in the panel.
      // "412 added" on an import where 690 rows were held back for a decision is
      // true and misleading at the same time.
      const reviewNote = conflictsKept || conflictsDeclined || conflictsUndecided
        ? ` Second G-DRG: ${conflictsKept} kept`
          + (conflictsDeclined ? `, ${conflictsDeclined} left out` : '')
          + (conflictsUndecided ? `, ${conflictsUndecided} still undecided` : '')
          + '.'
        : '';
      if (conflictsUndecided) {
        notify.error(
          'Imported, but some rows were held back',
          `${inserted} added, ${updated} updated.${reviewNote} `
          + 'Those rows are still not in the catalogue.'
        );
      } else {
        notify.success(
          'Catalogue updated',
          `${inserted} added, ${updated} updated`
          + `${skipped ? `, ${skipped} skipped` : ''}.${reviewNote}`
        );
      }
    } catch (err) {
      notify.error('Import failed', err?.response?.data?.error || err.message);
    } finally {
      setCommitting(false);
    }
  };

  // What Commit would actually do, given the mode. The alternate mode leaves one half
  // of the plan alone, and the button must not claim otherwise.
  //
  // WHICH half depends on the kind. 'insert-only' skips the UPDATES; investigations'
  // 'codes-only' skips the INSERTS — it is the reverse, because there the additions are
  // the unpriced ones and the updates are the safe half. Reading `mode !== 'upsert'` as
  // "insert only" put the wrong number on the button for that kind.
  const willWrite = useMemo(() => {
    if (!preview) return 0;
    const { insert = 0, update = 0 } = preview.counts;
    const base = mode === 'upsert' ? insert + update
      : (mode === 'codes-only' ? update : insert);
    // `insert` excludes the conflict rows — the server counts those separately —
    // so the ones the operator chose to keep are added back in. Without this the
    // button says "5,434" and the import writes 5,893.
    return base + keptRows;
  }, [preview, mode, keptRows]);

  const visibleRows = useMemo(() => {
    if (!preview) return [];
    if (rowFilter === 'problems') return preview.rows.filter((r) => r.action === 'blocked');
    if (rowFilter === 'changes')  return preview.rows.filter((r) => r.action === 'insert' || r.action === 'update');
    if (rowFilter === 'conflicts') return preview.rows.filter((r) => r.action === 'conflict');
    if (rowFilter === 'warnings') return preview.rows.filter((r) => r.warnings?.length);
    return preview.rows;
  }, [preview, rowFilter]);

  return (
    <SectionCard
      n={cfg.n} title={cfg.title} icon={cfg.icon} table={cfg.table}
      open={open} onToggle={() => setOpen((v) => !v)}
      // Folded, the header has to say what is inside — otherwise folding loses
      // the fact that a sheet is loaded and waiting to be committed.
      subtitle={!open && sheet
        ? `${sheet.rows.length} rows from ${file?.name || 'the sheet'}${committed ? ' · imported' : ''}`
        : null}
      right={
        <Button
          size="small"
          // Without this the click reaches the header and folds the card as the
          // download starts, so the template appears to have done nothing.
          onClick={(e) => { e.stopPropagation(); getTemplate(); }}
          disabled={templating}
          startIcon={templating
            ? <CircularProgress size={13} sx={{ color: BRAND.navy }} />
            : <DownloadIcon sx={{ fontSize: 16 }} />}
          sx={{ ...brandGhostButtonSx, border: `1px solid ${BRAND.line}`, px: 1.5, fontSize: 12.5 }}
        >
          Template
        </Button>
      }
    >
      {/* ── Pick a file ───────────────────────────────────────────────── */}
      {!file ? (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            display: 'grid', placeItems: 'center', gap: 1,
            px: 2, py: { xs: 3.5, sm: 5 },
            borderRadius: '12px', cursor: 'pointer',
            border: `1.5px dashed ${dragging ? BRAND.green : BRAND.line}`,
            background: dragging ? BRAND.greenSoft : BRAND.canvas,
            transition: 'background .15s ease, border-color .15s ease',
            '&:hover': { borderColor: BRAND.navyLight },
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={(e) => ingest(e.target.files?.[0])}
          />
          <Box sx={{
            width: 46, height: 46, borderRadius: '13px', display: 'grid', placeItems: 'center',
            background: BRAND.navySoft, color: BRAND.navy, mb: .5,
          }}>
            <UploadIcon sx={{ fontSize: 23 }} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: BRAND.ink }}>
            Drop the {cfg.title.toLowerCase()} spreadsheet here, or click to browse
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, textAlign: 'center' }}>
            {cfg.dropHint} Not sure of the layout? Download the template.
          </Typography>
        </Box>
      ) : (
        <FileBar
          file={file}
          parsing={parsing}
          sheet={sheet}
          sheets={parsed?.sheets || []}
          sheetIdx={sheetIdx}
          onSheet={pickSheet}
          onClear={reset}
        />
      )}

      {/* ── Options ───────────────────────────────────────────────────── */}
      {sheet && sheet.rows.length > 0 && (
        <Box sx={{
          display: 'grid', gap: 1.75, mt: 2.5,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) minmax(0,1.15fr)' },
          alignItems: 'start',
        }}>
          <Box>
            <FieldLabel>Release name *</FieldLabel>
            <TextField
              fullWidth size="small" placeholder="e.g. 2025 Tariff Update"
              value={releaseLabel}
              onChange={(e) => { setReleaseLabel(e.target.value); setLabelError(''); }}
              error={Boolean(labelError)}
              inputProps={{ maxLength: 160 }}
              sx={brandFieldSx}
            />
            <Hint>
              {labelError ? (
                <Box component="span" sx={{ color: BRAND.danger, fontWeight: 600 }}>{labelError}</Box>
              ) : (
                'Saved with the import and listed under Import record below, so this tariff can be identified months from now.'
              )}
            </Hint>
          </Box>

          <Box>
            <FieldLabel>{cfg.existingLabel}</FieldLabel>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(e, v) => v && setMode(v)}
              sx={{
                width: '100%',
                '& .MuiToggleButton-root': {
                  flex: 1, textTransform: 'none', fontSize: 12.5, fontWeight: 600,
                  borderColor: BRAND.line, color: BRAND.inkMuted, py: .875,
                  '&.Mui-selected': {
                    background: BRAND.navy, color: '#fff',
                    '&:hover': { background: BRAND.navyDark },
                  },
                },
              }}
            >
              {/*  ── THE SECOND MODE IS NOT THE SAME QUESTION EVERYWHERE ─────
                   For four of the five kinds it is "add new only". For
                   investigations it is the reverse — "codes only", meaning touch
                   what we have and add NOTHING — because the sheet carries no price
                   and an added test is offered on a claim immediately at nothing.

                   Hard-coding "Add new only" here would have sent that mode to the
                   endpoint as a word it refuses, and labelled the safe option with
                   the name of its opposite. */}
              {MODES(cfg).map(([value, label]) => (
                <ToggleButton key={value} value={value}>{label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Hint>{mode === 'upsert' ? cfg.upsertHint : cfg.insertOnlyHint}</Hint>
          </Box>
        </Box>
      )}

      {/* ── Actions ───────────────────────────────────────────────────────
          Above the table. The table opens by itself now, so the decision buttons
          are the first thing in reach when the results appear, and the operator
          scrolls DOWN into the rows from there rather than being sent to the
          bottom of a 437-row list to find Commit. */}
      {sheet && sheet.rows.length > 0 && (
        <Box sx={{
          display: 'flex', gap: 1.25, mt: 2.5, flexWrap: 'wrap',
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
        }}>
          <Button
            onClick={doPreview}
            disabled={previewing || committing}
            startIcon={previewing
              ? <CircularProgress size={14} sx={{ color: BRAND.navy }} />
              : <PreviewIcon sx={{ fontSize: 18 }} />}
            variant="outlined"
            sx={{ ...brandGhostButtonSx, flex: { xs: 1, sm: 'none' }, px: 2.25 }}
          >
            {previewing ? 'Checking…' : 'Check again'}
          </Button>
          <Tooltip
            title={!preview && !previewing
              ? 'The check against the catalogue did not complete — press Check again'
              : undecidedGroups.length
                ? `${undecidedGroups.length} ${undecidedGroups.length === 1 ? 'diagnosis' : 'diagnoses'} `
                  + 'below arrive under a G-DRG the catalogue does not hold. Say whether '
                  + 'to keep both — the review has a button for each kind at once.'
                : ''}
            arrow
          >
            <span style={{ display: 'inline-flex', flex: '1 1 auto' }}>
              <Button
                onClick={doCommit}
                // Held while a question is open. Committing with 308 diagnoses
                // unanswered would write the rest and leave 690 rows out, which
                // reads on screen as a completed import.
                disabled={!preview || committing || Boolean(committed)
                  || willWrite === 0 || undecidedGroups.length > 0}
                startIcon={committing
                  ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                  : <CommitIcon sx={{ fontSize: 18 }} />}
                sx={{ ...brandButtonSx, width: '100%', px: 2.5, py: .875 }}
              >
                {committing ? 'Writing…'
                  : committed ? 'Imported'
                  : undecidedGroups.length
                    ? `${undecidedGroups.length} to decide below`
                  : willWrite ? `Commit ${willWrite.toLocaleString()} ${cfg.item}${willWrite === 1 ? '' : 's'}`
                  : 'Nothing to commit'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Reading the file, then checking it. Two steps, one bar — from the
          operator's side it is a single wait between picking a file and seeing
          the table, and splitting it into two indicators would just flicker. */}
      {(parsing || previewing) && (
        <Box sx={{ mt: 2.5 }}>
          <LinearProgress sx={{
            borderRadius: 2, height: 5,
            background: BRAND.navySoft,
            '& .MuiLinearProgress-bar': { background: BRAND.navy },
          }} />
          <Typography sx={{ fontSize: 12, color: BRAND.inkMuted, mt: .875 }}>
            {parsing ? 'Reading the spreadsheet…' : 'Checking every row against the catalogue…'}
          </Typography>
        </Box>
      )}

      {/* ── Result ────────────────────────────────────────────────────── */}
      {preview && (
        <PreviewResult
          cfg={cfg}
          preview={preview}
          mode={mode}
          committed={committed}
          rowFilter={rowFilter}
          onFilter={setRowFilter}
          rows={visibleRows}
          sheet={sheet}
          decisions={decisions}
          onDecide={decide}
          onDecideMany={decideMany}
        />
      )}

    </SectionCard>
  );
}

// ── The picked file, and which tab of it ────────────────────────────────────
function FileBar({ file, parsing, sheet, sheets, sheetIdx, onSheet, onClear }) {
  const kb = (file.size / 1024).toFixed(0);
  return (
    <Box sx={{
      border: `1px solid ${BRAND.line}`, borderRadius: '12px',
      background: BRAND.canvas, p: { xs: 1.5, sm: 1.75 },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: '9px', flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: BRAND.greenSoft, color: BRAND.greenDark,
        }}>
          <FileIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: BRAND.ink }} noWrap>
            {file.name}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted }}>
            {kb} KB{sheets.length > 1 ? ` · ${sheets.length} sheets` : ''}
            {parsing ? ' · reading…' : ''}
          </Typography>
        </Box>
        <Tooltip title="Remove" arrow>
          <IconButton size="small" onClick={onClear} sx={{ color: BRAND.inkFaint }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {sheets.length > 1 && (
        <Box sx={{ mt: 1.5 }}>
          <FieldLabel>Sheet to import</FieldLabel>
          <TextField
            select fullWidth size="small"
            value={sheetIdx}
            onChange={(e) => onSheet(Number(e.target.value))}
            sx={brandFieldSx}
          >
            {sheets.map((s, i) => (
              <MenuItem key={`${s.name}-${i}`} value={i} disabled={s.rows.length === 0}>
                {s.name} — {s.rows.length
                  ? `${s.rows.length} row${s.rows.length === 1 ? '' : 's'}`
                  : 'no data rows'}
                {/* Selectable, but named for what it is. A guide or example tab
                    parses as cleanly as a tariff tab and there is no way to tell
                    them apart from a row count alone. */}
                {!s.looksLikeData && s.rows.length > 0 && (
                  <Box component="span" sx={{ ml: .75, fontSize: 11, color: BRAND.inkFaint }}>
                    (not a tariff sheet)
                  </Box>
                )}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {sheet && <SheetSummary sheet={sheet} />}
    </Box>
  );
}

// What was actually found in the sheet, said plainly. A header the importer
// could NOT find is the single most likely cause of a wrong import, so it is
// stated rather than left to be inferred from an odd row count.
function SheetSummary({ sheet }) {
  // Three ways the wrong sheet or the wrong tab shows up, in the order they are
  // worth hearing. Each one is stated before the per-row errors pile up, because
  // "97 rows could not be read" is a symptom and these are the cause.
  const problems = [];

  if (!sheet.headerDetected) {
    // No recognisable header AT ALL. Nothing below can be trusted to be in the
    // column it looks like, so this is the loudest of the three.
    problems.push(
      'No header row for this kind of sheet was found — falling back to the standard '
      + 'column order. Check the rows below landed in the right columns, and that this '
      + 'is the sheet you meant.'
    );
  } else if (sheet.missingColumns?.length) {
    problems.push(
      `Could not find ${sheet.missingColumns.length === 1 ? 'a column' : 'columns'} for `
      + `${sheet.missingColumns.join(', ')}. Either this is the wrong sheet, or the `
      + 'headers are worded differently — compare it against the template.'
    );
  }

  // The case a missing column cannot catch: a procedure sheet has every column a
  // diagnosis sheet needs, plus a procedure column. The extra column is the tell.
  if (sheet.foreignColumns?.length) {
    problems.push(
      `This sheet also has ${sheet.foreignColumns.join(' and ')}, which this section `
      + 'does not import. It may belong to one of the other sections.'
    );
  }

  if (!sheet.rows.length) {
    problems.push('No data rows on this sheet.');
  }

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${BRAND.line}` }}>
      <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap', alignItems: 'center' }}>
        <MiniChip label={`${sheet.rows.length} rows`} strong />
        {sheet.headerDetected && <MiniChip label={`header on row ${sheet.headerRow}`} />}
        {sheet.hasCategoryColumn && <MiniChip label="specialty column found" />}
        {sheet.skippedBlank > 0 && <MiniChip label={`${sheet.skippedBlank} blank rows skipped`} />}
      </Box>
      {problems.map((p) => (
        <Box key={p} sx={{ display: 'flex', gap: .75, mt: 1, alignItems: 'flex-start' }}>
          <WarnIcon sx={{ fontSize: 15, color: BRAND.warning, mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: BRAND.inkMuted, lineHeight: 1.55 }}>{p}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Preview / result ────────────────────────────────────────────────────────
function PreviewResult({
  cfg, preview, mode, committed, rowFilter, onFilter, rows,
  sheet, decisions = {}, onDecide, onDecideMany,
}) {
  const c = preview.counts;
  //  Which half the chosen mode leaves alone. 'insert-only' skips the updates;
  //  'codes-only' skips the inserts. Labelling both as "insert only" told the operator
  //  the opposite of the truth on an investigations sheet — the tile said the updates
  //  were being skipped when they were the only thing being written.
  const insertOnly = mode === 'insert-only';
  const codesOnly = mode === 'codes-only';
  const conflictGroups = preview.conflictGroups || [];

  const tiles = [
    { key: 'insert',
      label: codesOnly ? 'New (not added)' : 'To add',
      value: c.insert },
    { key: 'update',    label: insertOnly ? 'Would change (skipped)' : 'To update', value: c.update },
    { key: 'unchanged', label: 'Already correct', value: c.unchanged },
    // The fourth tile is whichever question is actually outstanding. A "Problems:
    // 0" tile next to 308 unanswered decisions puts the reassuring number in the
    // prominent slot and the one that needs acting on nowhere.
    ...(c.conflict > 0
      ? [{ key: 'conflict', label: 'Your call', value: c.conflict }]
      : [{ key: 'blocked', label: 'Problems', value: c.blocked }]),
  ];

  const filters = [
    { key: 'changes',  label: `Changes (${c.insert + c.update})` },
    ...(c.conflict > 0 ? [{ key: 'conflicts', label: `Your call (${c.conflict})` }] : []),
    { key: 'problems', label: `Problems (${c.blocked})` },
    { key: 'warnings', label: `Warnings (${c.warnings})` },
    { key: 'all',      label: `All (${c.total})` },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      {committed && (
        <Box sx={{
          display: 'flex', gap: 1.25, alignItems: 'flex-start',
          p: 1.75, mb: 2, borderRadius: '11px',
          background: BRAND.greenSoft, border: `1px solid ${BRAND.green}44`,
        }}>
          <OkIcon sx={{ fontSize: 19, color: BRAND.greenDark, flexShrink: 0, mt: '1px' }} />
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 750, color: BRAND.ink }}>
              Written to the catalogue
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.6 }}>
              {committed.inserted} added, {committed.updated} updated
              {committed.skipped ? `, ${committed.skipped} skipped` : ''}
              {/* Procedures only. Worth its own clause: the ICD-10 indications are
                  a separate table from the priced rows, so an import that changed
                  no tariff can still have stored hundreds of them. */}
              {committed.indications > 0
                && `, ${committed.indications.toLocaleString()} ICD-10 indications stored`}.
              {' '}The figures below are what was checked, not a fresh reading —
              run the check again to see the catalogue as it now stands.
            </Typography>
          </Box>
        </Box>
      )}

      <SectionLabel>What this import would do</SectionLabel>
      <Box sx={{
        display: 'grid', gap: 1,
        gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' },
        mb: 2,
      }}>
        {tiles.map((t) => {
          const meta = ACTION_META[t.key];
          const Icon = meta.icon;
          const muted = t.value === 0 || (insertOnly && t.key === 'update');
          return (
            <Box key={t.key} sx={{
              p: 1.5, borderRadius: '11px',
              border: `1px solid ${muted ? BRAND.line : `${meta.color}55`}`,
              background: muted ? BRAND.canvas : meta.soft,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: .625, mb: .375 }}>
                <Icon sx={{ fontSize: 15, color: muted ? BRAND.inkFaint : meta.color }} />
                <Typography sx={{
                  fontSize: 10.5, fontWeight: 800, letterSpacing: '.5px', textTransform: 'uppercase',
                  color: muted ? BRAND.inkFaint : BRAND.inkMuted,
                }}>
                  {t.label}
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: 22, fontWeight: 800, lineHeight: 1.1,
                color: muted ? BRAND.inkFaint : BRAND.ink,
              }}>
                {t.value}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* The procedure sheet has more rows than the table needs — one line per
          procedure × indication. Said plainly and up front: a 97-to-14 reduction
          that nobody mentioned looks exactly like data loss to whoever counts the
          rows in the spreadsheet afterwards. */}
      {c.sourceLines > 0 && c.sourceLines !== c.total && (
        <Notice tone="info">
          The sheet has <strong>{c.sourceLines.toLocaleString()} rows</strong> describing{' '}
          <strong>{c.total.toLocaleString()} {c.total === 1 ? 'procedure' : 'procedures'}</strong>
          {c.indications > 0 && <> and {c.indications.toLocaleString()} ICD-10 indications</>}.
          {' '}A procedure is priced once, not once per diagnosis, so its rows are
          combined into one. The ICD-10 indications are kept in full, in their own
          table — that is what lets the claims editor offer an ICD-10 for a
          procedure.
        </Notice>
      )}

      {preview.unresolvedCategories?.length > 0 && (
        <Notice tone="warning">
          No specialty category is known for {preview.unresolvedCategories.join(', ')}.
          Those rows import with the category left blank — nothing else is affected.
          To fill it, add a <strong>SPECIALTY CATEGORY</strong> column to the sheet
          (the template has one) and check again.
        </Notice>
      )}

      {c.blocked > 0 && (
        <Notice tone="danger">
          {c.blocked} row{c.blocked === 1 ? '' : 's'} cannot be read and will be
          skipped. Fix them in the spreadsheet and check again if they matter —
          committing now imports everything else and leaves them out.
        </Notice>
      )}

      {/* A header that matched two of the importer's patterns. Reported because
          the missing-column check above cannot see it — the field WAS found, just
          on the wrong column — and that is how an ICD-10 chapter heading became
          the ICD-10 code of 6,537 rows with no warning at all. */}
      {sheet?.collidedColumns?.length > 0 && (
        <Notice tone="danger">
          Two things were read from the same column, so a column the importer needs
          was never read at all:{' '}
          {sheet.collidedColumns.map((x) => (
            <span key={x.column}>
              <strong>“{x.header}”</strong> was taken as both {x.fields.join(' and ')}
            </span>
          ))}. Rename the headers so each is distinct and check again — importing
          now would write the wrong value into every row.
        </Notice>
      )}

      {/* ── The decision ────────────────────────────────────────────────── */}
      {conflictGroups.length > 0 && (
        <ConflictReview
          groups={conflictGroups}
          decisions={decisions}
          onDecide={onDecide}
          onDecideMany={onDecideMany}
          committed={Boolean(committed)}
        />
      )}

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: .75, flexWrap: 'wrap',
        mt: 2.5, mb: 1.25,
      }}>
        <SectionLabel sx={{ mb: 0, mr: .5 }}>Rows</SectionLabel>
        {filters.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            onClick={() => onFilter(f.key)}
            sx={{
              height: 26, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
              background: rowFilter === f.key ? BRAND.navy : BRAND.canvas,
              color: rowFilter === f.key ? '#fff' : BRAND.inkMuted,
              border: `1px solid ${rowFilter === f.key ? BRAND.navy : BRAND.line}`,
              '&:hover': {
                background: rowFilter === f.key ? BRAND.navyDark : BRAND.navySoft,
              },
            }}
          />
        ))}
      </Box>

      <RowTable cfg={cfg} rows={rows} insertOnly={insertOnly} />
    </Box>
  );
}

// ── Same diagnosis, a G-DRG the catalogue does not hold for it ───────────────
//  The question the operator has to answer before these rows are written, and the
//  reason it is a question at all:
//
//  On the ICD-matched sheet, 2,152 of 3,297 ICD-10 codes carry more than one
//  G-DRG. B74 "Filariasis" carries ten. That is not a defect — it is the
//  adult/child tariff band and the alternative procedures for one condition — so
//  the importer's key stays (ICD-10 + G-DRG) and a second G-DRG is not blocked.
//
//  What IS a decision is the sheet disagreeing with the CATALOGUE: 308 diagnoses,
//  690 rows. Grouped by ICD-10 because a person decides per diagnosis, sorted
//  worst-kind first, and answerable per kind in one press — 241 of the 308 are
//  the near-mechanical kinds, and burying the 67 that need reading among them is
//  how a review gets clicked through rather than done.
function ConflictReview({ groups, decisions, onDecide, onDecideMany, committed }) {
  const [kindFilter, setKindFilter] = useState('all');
  const [showDecided, setShowDecided] = useState(false);

  const decidedCount = groups.filter((g) => decisions[g.icd]).length;
  const keptCount = groups.filter((g) => decisions[g.icd] === 'keep').length;
  const skipCount = groups.filter((g) => decisions[g.icd] === 'skip').length;
  const rowsWaiting = groups
    .filter((g) => !decisions[g.icd])
    .reduce((n, g) => n + g.proposed.length, 0);

  const byKind = useMemo(() => {
    const m = {};
    for (const k of CONFLICT_KINDS) m[k.key] = groups.filter((g) => g.kind === k.key);
    return m;
  }, [groups]);

  const visible = useMemo(() => groups.filter((g) => {
    if (kindFilter !== 'all' && g.kind !== kindFilter) return false;
    if (!showDecided && decisions[g.icd]) return false;
    return true;
  }), [groups, kindFilter, showDecided, decisions]);

  //  A cap, and it says so. 308 groups of five lines each is 1,500 rows of DOM,
  //  and a list that silently stops at 200 reads as "that was all of them".
  const CAP = 200;
  const shown = visible.slice(0, CAP);

  return (
    <Box sx={{
      mt: 2.5, borderRadius: '12px', overflow: 'hidden',
      border: `1px solid ${'#B4690E'}44`, background: '#FDF3E3',
    }}>
      {/* ── What is being asked ─────────────────────────────────────────── */}
      <Box sx={{ p: { xs: 1.75, sm: 2 } }}>
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
          <ForkIcon sx={{ fontSize: 20, color: '#B4690E', flexShrink: 0, mt: '2px' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 750, color: BRAND.ink }}>
              {groups.length} {groups.length === 1 ? 'diagnosis' : 'diagnoses'} arrive
              under a G-DRG the catalogue does not hold for them
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.65, mt: .375 }}>
              The catalogue already bills each of these on a different G-DRG. Keeping
              both is often right — it is how one diagnosis is billed differently for
              an adult and a child — but it can also mean the sheet and the catalogue
              disagree about how a diagnosis should be billed at all.{' '}
              <strong>Nothing here is written until you say.</strong>
            </Typography>
          </Box>
        </Box>

        {/* ── Answer a whole kind at once ───────────────────────────────── */}
        <Box sx={{
          display: 'grid', gap: 1, mt: 1.75,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' },
        }}>
          {CONFLICT_KINDS.map((k) => {
            const list = byKind[k.key];
            if (!list.length) return null;
            const undecided = list.filter((g) => !decisions[g.icd]);
            return (
              <Box key={k.key} sx={{
                p: 1.375, borderRadius: '10px', background: '#fff',
                border: `1px solid ${k.color}40`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: .625 }}>
                  <Typography sx={{ fontSize: 19, fontWeight: 800, color: k.color, lineHeight: 1 }}>
                    {list.length}
                  </Typography>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 800, letterSpacing: '.3px',
                    textTransform: 'uppercase', color: BRAND.inkMuted,
                  }}>
                    {k.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted, lineHeight: 1.55, mt: .625 }}>
                  {k.blurb}
                </Typography>
                <Box sx={{ display: 'flex', gap: .75, mt: 1.125 }}>
                  <Button
                    size="small" disabled={committed || !undecided.length}
                    onClick={() => onDecideMany(undecided.map((g) => g.icd), 'keep')}
                    sx={{
                      ...brandGhostButtonSx, flex: 1, fontSize: 11.5, px: 1,
                      border: `1px solid ${BRAND.green}66`, color: BRAND.greenDark,
                    }}
                  >
                    Keep both
                  </Button>
                  <Button
                    size="small" disabled={committed || !undecided.length}
                    onClick={() => onDecideMany(undecided.map((g) => g.icd), 'skip')}
                    sx={{
                      ...brandGhostButtonSx, flex: 1, fontSize: 11.5, px: 1,
                      border: `1px solid ${BRAND.line}`, color: BRAND.inkMuted,
                    }}
                  >
                    Leave out
                  </Button>
                </Box>
                {undecided.length !== list.length && (
                  <Typography sx={{ fontSize: 10.5, color: BRAND.inkFaint, mt: .625 }}>
                    {list.length - undecided.length} of {list.length} already decided
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ── Where the review stands ───────────────────────────────────── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          mt: 1.75, pt: 1.5, borderTop: `1px solid ${'#B4690E'}2E`,
        }}>
          <DoneAllIcon sx={{
            fontSize: 17,
            color: decidedCount === groups.length ? BRAND.greenDark : BRAND.inkFaint,
          }} />
          <Typography sx={{ fontSize: 12.5, color: BRAND.ink, fontWeight: 700 }}>
            {decidedCount} of {groups.length} decided
          </Typography>
          {keptCount > 0 && (
            <MiniChip label={`${keptCount} keeping both`} strong />
          )}
          {skipCount > 0 && <MiniChip label={`${skipCount} left out`} />}
          {rowsWaiting > 0 && (
            <Typography sx={{ fontSize: 12, color: '#8A5510', fontWeight: 650 }}>
              — {rowsWaiting} row{rowsWaiting === 1 ? '' : 's'} still waiting
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {decidedCount > 0 && !committed && (
            <Button
              size="small"
              onClick={() => onDecideMany(groups.map((g) => g.icd), null)}
              sx={{ ...brandGhostButtonSx, fontSize: 11.5, px: 1.25 }}
            >
              Start over
            </Button>
          )}
        </Box>
      </Box>

      {/* ── The list ──────────────────────────────────────────────────────── */}
      <Box sx={{
        borderTop: `1px solid ${'#B4690E'}2E`, background: '#fff',
        p: { xs: 1.25, sm: 1.5 },
      }}>
        <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap', alignItems: 'center', mb: 1.25 }}>
          {[{ key: 'all', label: `All (${groups.length})` },
            ...CONFLICT_KINDS
              .filter((k) => byKind[k.key].length)
              .map((k) => ({ key: k.key, label: `${k.label} (${byKind[k.key].length})` }))]
            .map((f) => (
              <Chip
                key={f.key} label={f.label} size="small"
                onClick={() => setKindFilter(f.key)}
                sx={{
                  height: 25, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: kindFilter === f.key ? BRAND.navy : BRAND.canvas,
                  color: kindFilter === f.key ? '#fff' : BRAND.inkMuted,
                  border: `1px solid ${kindFilter === f.key ? BRAND.navy : BRAND.line}`,
                }}
              />
            ))}
          <Box sx={{ flex: 1 }} />
          <Chip
            label={showDecided ? 'Hiding nothing' : 'Decided ones hidden'}
            size="small" onClick={() => setShowDecided((v) => !v)}
            sx={{
              height: 25, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              background: BRAND.canvas, color: BRAND.inkMuted,
              border: `1px solid ${BRAND.line}`,
            }}
          />
        </Box>

        {visible.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, py: 2, textAlign: 'center' }}>
            {decidedCount === groups.length
              ? 'Every one decided. Commit will write the ones you kept.'
              : 'Nothing matches that filter.'}
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: 1 }}>
            {shown.map((g) => (
              <ConflictRow
                key={g.icd} g={g} decision={decisions[g.icd]}
                onDecide={onDecide} committed={committed}
              />
            ))}
          </Box>
        )}

        {visible.length > CAP && (
          <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted, mt: 1.25, textAlign: 'center' }}>
            Showing {CAP} of {visible.length}. Answer a whole kind above, or narrow
            the filter — the remaining {visible.length - CAP} are still counted and
            still waiting.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// One diagnosis, both sides of it, and the two buttons.
function ConflictRow({ g, decision, onDecide, committed }) {
  const kind = CONFLICT_KINDS.find((k) => k.key === g.kind) || CONFLICT_KINDS[0];
  const decided = Boolean(decision);
  return (
    <Box sx={{
      border: `1px solid ${decided ? BRAND.line : `${kind.color}44`}`,
      borderRadius: '10px', p: 1.25,
      background: decided ? BRAND.canvas : '#fff',
      opacity: decided ? .72 : 1,
    }}>
      <Box sx={{
        display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap', mb: .875,
      }}>
        <Typography sx={{
          fontFamily: MONO, fontSize: 12.5, fontWeight: 750, color: BRAND.ink,
        }}>
          {g.icd}
        </Typography>
        <Typography sx={{
          fontSize: 12.5, fontWeight: 650, color: BRAND.ink, flex: 1, minWidth: 140,
        }}>
          {g.name}
        </Typography>
        <Box sx={{
          px: .75, py: .125, borderRadius: '5px', fontSize: 10,
          fontWeight: 800, letterSpacing: '.3px', textTransform: 'uppercase',
          background: `${kind.color}1A`, color: kind.color, whiteSpace: 'nowrap',
        }}>
          {kind.label}
        </Box>
      </Box>

      {/* Both sides, side by side on a wide screen and stacked on a phone. The
          decision is a comparison, so the two lists must be readable together. */}
      <Box sx={{
        display: 'grid', gap: 1,
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1fr) minmax(0,1fr)' },
      }}>
        <Box>
          <Typography sx={{
            fontSize: 10, fontWeight: 800, letterSpacing: '.4px',
            textTransform: 'uppercase', color: BRAND.inkFaint, mb: .5,
          }}>
            Catalogue bills it as
          </Typography>
          {g.held.map((h, i) => (
            <GdrgLine key={`${h.core}-${i}`} core={h.core} age={h.age_cat}
              spe={h.spe} text={h.name} />
          ))}
        </Box>
        <Box>
          <Typography sx={{
            fontSize: 10, fontWeight: 800, letterSpacing: '.4px',
            textTransform: 'uppercase', color: '#B4690E', mb: .5,
          }}>
            The sheet adds
          </Typography>
          {g.proposed.map((p, i) => (
            <GdrgLine key={`${p.core}-${i}`} core={p.core} age={p.age_cat}
              spe={p.spe} text={p.gdrgName || p.name} added />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: .75, mt: 1.125, alignItems: 'center' }}>
        {decided && (
          <Typography sx={{
            fontSize: 11.5, fontWeight: 750, mr: .5,
            color: decision === 'keep' ? BRAND.greenDark : BRAND.inkMuted,
          }}>
            {decision === 'keep'
              ? `Keeping both — ${g.proposed.length} row${g.proposed.length === 1 ? '' : 's'} will be added`
              : 'Left out — nothing written for this diagnosis'}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small" disabled={committed}
          onClick={() => onDecide(g.icd, decision === 'keep' ? null : 'keep')}
          sx={{
            ...brandGhostButtonSx, fontSize: 11.5, px: 1.25,
            border: `1px solid ${decision === 'keep' ? BRAND.green : BRAND.line}`,
            background: decision === 'keep' ? BRAND.greenSoft : 'transparent',
            color: decision === 'keep' ? BRAND.greenDark : BRAND.inkMuted,
          }}
        >
          Keep both
        </Button>
        <Button
          size="small" disabled={committed}
          onClick={() => onDecide(g.icd, decision === 'skip' ? null : 'skip')}
          sx={{
            ...brandGhostButtonSx, fontSize: 11.5, px: 1.25,
            border: `1px solid ${decision === 'skip' ? BRAND.inkMuted : BRAND.line}`,
            background: decision === 'skip' ? BRAND.canvas : 'transparent',
            color: BRAND.inkMuted,
          }}
        >
          Leave out
        </Button>
      </Box>
    </Box>
  );
}

function GdrgLine({ core, age, spe, text, added }) {
  const tone = AGE_TONE[age] || AGE_TONE.Both;
  return (
    <Box sx={{ display: 'flex', gap: .625, alignItems: 'baseline', mb: .375, flexWrap: 'wrap' }}>
      <Typography sx={{
        fontFamily: MONO, fontSize: 11.5, fontWeight: 750,
        color: added ? '#8A5510' : BRAND.ink,
      }}>
        {core}
      </Typography>
      <Box sx={{
        px: .5, borderRadius: '4px', fontSize: 9.5, fontWeight: 800,
        background: tone.bg, color: tone.fg,
      }}>
        {age}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted, minWidth: 0 }}>
        {spe}{text ? ` · ${text}` : ''}
      </Typography>
    </Box>
  );
}

const AGE_TONE = {
  Adult: { bg: BRAND.navySoft, fg: BRAND.navy },
  Child: { bg: '#FDF0E2',      fg: '#8A5510' },
  Both:  { bg: BRAND.canvas,   fg: BRAND.inkMuted },
};

// MainLayout's AppBar is position:fixed, so the document is what scrolls and a
// sticky header has to clear the bar by exactly its height — theme.mixins.toolbar
// is 56 on xs and 64 from sm up. Flush is deliberate: a larger offset leaves a
// strip of rows visible above the header, which looks like a rendering fault.
const STICKY_TOP = { xs: 56, sm: 64 };

// A sheet big enough to lock the browser up is not reviewable by eye anyway, so
// the render is capped — and the cap is stated on screen rather than silently
// truncating a list the operator is checking for correctness.
const MAX_RENDERED = 2000;

// The table opens to its full height and the PAGE scrolls, rather than the old
// 460px inner scroller. Two scrollbars for one list is confusing, and the whole
// point of this screen is reading every row before committing — a viewport into
// the middle of the list fights that.
//
// Which is why the wrapper must not create a scroll context: any `overflow`
// other than visible on an ancestor makes THAT box the sticky header's
// scrollport, and since it never scrolls, the header never sticks. So no
// overflow:hidden here (the corners are rounded on the cells instead) and
// overflow-x only on phones, where the six columns genuinely cannot fit and a
// header that scrolls sideways out of view is the lesser problem.
function RowTable({ cfg, rows, insertOnly }) {
  if (!rows.length) {
    return (
      <Box sx={{
        py: 4, textAlign: 'center', borderRadius: '11px',
        border: `1px dashed ${BRAND.line}`, background: BRAND.canvas,
      }}>
        <Typography sx={{ fontSize: 13, color: BRAND.inkMuted }}>
          Nothing in this view.
        </Typography>
      </Box>
    );
  }

  const shown = rows.slice(0, MAX_RENDERED);

  return (
    <>
      <Box sx={{
        border: `1px solid ${BRAND.line}`, borderRadius: '11px',
        // Horizontal only, and only where the columns cannot fit. At sm and up
        // this stays `visible` so the sticky header below can attach to the
        // viewport instead of to this box.
        overflowX: { xs: 'auto', sm: 'visible' },
      }}>
        <Box component="table" sx={{
          width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 640,
          '& th': {
            position: 'sticky', top: STICKY_TOP, zIndex: 2,
            // Opaque, not tinted-transparent: rows scroll UNDER this now, and a
            // translucent header would show them through it.
            background: BRAND.canvas,
            borderBottom: `1px solid ${BRAND.line}`,
            // Reads as floating above the rows once it is pinned. Harmless at
            // rest, since the header sits against the card edge anyway.
            boxShadow: `0 1px 0 ${BRAND.line}, 0 6px 12px -8px rgba(15,31,61,.28)`,
            fontSize: 10.5, fontWeight: 800, letterSpacing: '.5px',
            textTransform: 'uppercase', color: BRAND.inkFaint,
            textAlign: 'left', padding: '9px 12px', whiteSpace: 'nowrap',
          },
          // borderCollapse is 'separate' so the sticky header keeps its own
          // border while scrolling — collapsed borders belong to the table, not
          // the cell, and vanish the moment the row detaches.
          '& td': {
            borderBottom: `1px solid ${BRAND.line}`,
            fontSize: 12.5, color: BRAND.ink, padding: '9px 12px', verticalAlign: 'top',
          },
          '& tbody tr:last-of-type td': { borderBottom: 'none' },
          '& tbody tr:hover td': { background: BRAND.canvas },
          // The wrapper can no longer clip to its own radius, so the corners are
          // rounded on the cells that sit in them.
          '& thead th:first-of-type': { borderTopLeftRadius: '10px' },
          '& thead th:last-of-type':  { borderTopRightRadius: '10px' },
          '& tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: '10px' },
          '& tbody tr:last-of-type td:last-of-type':  { borderBottomRightRadius: '10px' },
        }}>
          <thead>
            <tr>
              {cfg.columns.map((col) => (
                // The primary column gets no width so it takes the slack; the
                // rest are fixed, which keeps codes and figures from jittering
                // between rows.
                <th key={col.key} style={col.primary ? undefined : { width: col.width }}>
                  {col.label}
                </th>
              ))}
              <th style={{ width: 190 }}>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <RowLine
                key={`${r.rowNumber}-${r.code || r.icd || ''}`}
                cfg={cfg} r={r} insertOnly={insertOnly}
              />
            ))}
          </tbody>
        </Box>
      </Box>

      {rows.length > shown.length && (
        <Typography sx={{ fontSize: 12, color: BRAND.inkMuted, mt: 1.25, lineHeight: 1.6 }}>
          Showing the first {shown.length.toLocaleString()} of {rows.length.toLocaleString()} rows
          — too many to read at once. Use the <strong>Problems</strong> and{' '}
          <strong>Changes</strong> filters above to narrow it down. Committing still
          covers every row, not just the ones listed here.
        </Typography>
      )}
    </>
  );
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

// One cell, rendered from its column definition. `primary` marks the column that
// carries the row's errors and warnings underneath it — the widest one, so the
// messages have room to read as sentences rather than wrapping every third word.
function RowCell({ col, r }) {
  const raw = r[col.key];
  const change = col.changeOf ? r.changes?.[col.changeOf] : null;

  const style = {
    ...(col.faint ? { color: BRAND.inkFaint } : {}),
    ...(col.bold ? { fontWeight: 700 } : {}),
    ...(col.mono ? { fontFamily: MONO, fontVariantNumeric: 'tabular-nums' } : {}),
  };

  if (col.tone === 'age') {
    const tone = AGE_TONE[raw] || AGE_TONE.Both;
    return (
      <td>
        <Box component="span" sx={{
          display: 'inline-block', px: .875, py: .25, borderRadius: '6px',
          fontSize: 11, fontWeight: 700, background: tone.bg, color: tone.fg,
        }}>
          {raw}
        </Box>
      </td>
    );
  }

  // How many spreadsheet rows folded into this one procedure. Shown as a count
  // with its ICD codes on hover, because "11" on its own invites the question.
  if (col.tone === 'count') {
    const codes = (r.indications || []).map((i) => i.icd).filter(Boolean);
    return (
      <td>
        {codes.length ? (
          <Tooltip arrow title={codes.join(', ')}>
            <Box component="span" sx={{
              display: 'inline-block', px: .875, py: .25, borderRadius: '6px',
              fontSize: 11, fontWeight: 700, cursor: 'help',
              background: BRAND.navySoft, color: BRAND.navy,
            }}>
              {codes.length} ICD-10
            </Box>
          </Tooltip>
        ) : (
          <Box component="span" sx={{ fontSize: 11, color: BRAND.inkFaint }}>none listed</Box>
        )}
      </td>
    );
  }

  if (col.money) {
    return (
      <td style={{ fontVariantNumeric: 'tabular-nums' }}>
        <Box sx={{ fontWeight: 700 }}>{Number(raw || 0).toFixed(2)}</Box>
        {change && (
          // Direction is spelled with a word as well as a number — an arrow glyph
          // alone is easy to misread at 11px.
          <Box sx={{ fontSize: 11, color: BRAND.inkMuted, mt: .25, whiteSpace: 'nowrap' }}>
            {Number(change.to) > Number(change.from) ? 'up from' : 'down from'}{' '}
            {Number(change.from).toFixed(2)}
          </Box>
        )}
      </td>
    );
  }

  return (
    <td style={style}>
      {raw === '' || raw === null || raw === undefined
        ? <span style={{ color: BRAND.inkFaint }}>{col.primary ? '(blank)' : '—'}</span>
        : raw}

      {change && (
        <Box sx={{ fontSize: 11.5, color: BRAND.inkMuted, mt: .25 }}>
          was {change.from}
        </Box>
      )}

      {col.primary && (
        <>
          {r.errors?.map((e) => (
            <Box key={e} sx={{
              display: 'flex', gap: .5, alignItems: 'flex-start',
              fontSize: 11.5, color: BRAND.danger, mt: .375, fontWeight: 600,
            }}>
              <ErrorIcon sx={{ fontSize: 13, mt: '1px', flexShrink: 0 }} />{e}
            </Box>
          ))}
          {r.warnings?.map((w) => (
            <Box key={w} sx={{
              display: 'flex', gap: .5, alignItems: 'flex-start',
              fontSize: 11.5, color: '#8A5510', mt: .375,
            }}>
              <WarnIcon sx={{ fontSize: 13, mt: '1px', flexShrink: 0 }} />{w}
            </Box>
          ))}
        </>
      )}
    </td>
  );
}

function RowLine({ cfg, r, insertOnly }) {
  const meta = ACTION_META[r.action] || ACTION_META.unchanged;
  const Icon = meta.icon;
  // Under insert-only an updatable row is not written, so calling it "Update"
  // would be a lie about what Commit is about to do.
  const label = insertOnly && r.action === 'update' ? 'Left as is' : meta.label;

  return (
    <tr>
      {cfg.columns.map((col) => <RowCell key={col.key} col={col} r={r} />)}
      <td>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
          <Icon sx={{ fontSize: 15, color: meta.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND.ink }}>
            {label}
          </Typography>
        </Box>
        {r.action === 'update' && (
          <Box sx={{ fontSize: 11, color: BRAND.inkMuted, mt: .25 }}>
            {Object.keys(r.changes).join(', ')}
          </Box>
        )}
      </td>
    </tr>
  );
}

// ── Import record ───────────────────────────────────────────────────────────
//  Every tariff sheet ever loaded, by name. Its own card rather than a block
//  inside Services, so folding the import machinery away does not also hide the
//  record — the common visit to this screen is not an import at all, it is
//  someone asking which tariff the facility is currently billing on.
function ImportRecordSection({ refreshTokens }) {
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState(null);      // null = not loaded yet
  const [error, setError] = useState('');

  // Every sheet type's record in one list. They share a table and a route shape,
  // so this is two requests rather than two panels — "which tariff are we on?"
  // and "which diagnosis set are we on?" are the same question asked twice, and
  // reading them side by side is how you notice one is a release behind.
  const kinds = Object.keys(SHEET_KINDS);
  const tokenSig = kinds.map((k) => refreshTokens?.[k] || 0).join(',');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(kinds.map(async (kind) => {
          const { data } = await axios.get(nhisCatalogueImportUrls(kind).history);
          return (data.imports || []).map((r) => ({ ...r, kind }));
        }));
        if (cancelled) return;
        // Newest first across both kinds.
        const merged = results.flat().sort((a, b) =>
          String(b.importedAt).localeCompare(String(a.importedAt)) || b.id - a.id);
        setRows(merged);
        setError('');
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenSig]);

  const latest = rows?.[0];

  return (
    <SectionCard
      title="Import record" icon={HistoryIcon}
      open={open} onToggle={() => setOpen((v) => !v)}
      // The one fact worth showing while folded: what is currently loaded.
      subtitle={!open && latest
        ? `Latest: ${latest.releaseLabel} · ${formatWhen(latest.importedAt)}`
        : null}
      right={rows?.length > 0 ? (
        <Chip
          size="small"
          label={`${rows.length} release${rows.length === 1 ? '' : 's'}`}
          sx={{
            height: 24, fontSize: 11, fontWeight: 700,
            background: BRAND.navySoft, color: BRAND.navy, border: 'none',
          }}
        />
      ) : null}
    >
      <ImportHistory rows={rows} error={error} />
    </SectionCard>
  );
}

function ImportHistory({ rows, error }) {
  const [openId, setOpenId] = useState(null);

  return (
    <Box>
      {error && (
        <Notice tone="danger">
          Could not load the import record: {error}
        </Notice>
      )}

      {rows === null && !error && (
        <Typography sx={{ fontSize: 12.5, color: BRAND.inkFaint }}>Loading…</Typography>
      )}

      {rows?.length === 0 && (
        <Box sx={{
          py: 3, px: 2, textAlign: 'center', borderRadius: '11px',
          border: `1px dashed ${BRAND.line}`, background: BRAND.canvas,
        }}>
          <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, lineHeight: 1.6 }}>
            Nothing has been imported yet. Once you commit a sheet, it is recorded
            here by name — with what it changed, when, and who ran it.
          </Typography>
        </Box>
      )}

      {/* Keyed on kind AND id: the two sheet types number their records
          independently, so id alone collides across them. */}
      {rows?.map((r) => (
        <HistoryRow
          key={`${r.kind}-${r.id}`}
          r={r}
          open={openId === `${r.kind}-${r.id}`}
          onToggle={() => setOpenId(openId === `${r.kind}-${r.id}` ? null : `${r.kind}-${r.id}`)}
        />
      ))}
    </Box>
  );
}

function HistoryRow({ r, open, onToggle }) {
  const hasDetail = r.hasDetail;
  const kindCfg = SHEET_KINDS[r.kind];
  const KindIcon = kindCfg?.icon || HistoryIcon;
  // Fetched on first expand and kept. The list response carries counts only —
  // a full release stores ~59 KB of row detail, and loading twenty-five of those
  // to render a collapsed list would be several megabytes nobody asked for.
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');

  React.useEffect(() => {
    if (!open || detail || detailError) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(nhisCatalogueImportUrls(r.kind).record(r.id));
        if (!cancelled) setDetail(data.detail);
      } catch (err) {
        if (!cancelled) setDetailError(err?.response?.data?.error || err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [open, detail, detailError, r.id, r.kind]);

  return (
    <Box sx={{
      border: `1px solid ${BRAND.line}`, borderRadius: '11px',
      background: BRAND.surface, mb: 1,
    }}>
      <Box
        onClick={hasDetail ? onToggle : undefined}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5,
          cursor: hasDetail ? 'pointer' : 'default',
          borderRadius: '11px',
          '&:hover': hasDetail ? { background: BRAND.canvas } : {},
        }}
      >
        <Box sx={{
          width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: BRAND.navySoft, color: BRAND.navy,
        }}>
          {/* The icon of the sheet type, not a generic one — with two kinds in
              one list, "which catalogue did this touch?" is the first question. */}
          <KindIcon sx={{ fontSize: 17 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: .75, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 750, color: BRAND.ink }} noWrap>
              {r.releaseLabel}
            </Typography>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: '.4px', flexShrink: 0,
              textTransform: 'uppercase', color: BRAND.inkFaint,
            }}>
              {kindCfg?.title || r.kind}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted }} noWrap>
            {formatWhen(r.importedAt)}
            {r.importedBy ? ` · ${r.importedBy}` : ''}
            {r.fileName ? ` · ${r.fileName}` : ''}
            {r.sheetName ? ` (${r.sheetName})` : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {r.inserted > 0 && <CountChip icon={NewIcon}    n={r.inserted} label="added"   color={BRAND.green} />}
          {r.updated  > 0 && <CountChip icon={ChangeIcon} n={r.updated}  label="updated" color={BRAND.info} />}
          {r.skipped  > 0 && <CountChip icon={ErrorIcon}  n={r.skipped}  label="skipped" color={BRAND.danger} />}
          {r.inserted === 0 && r.updated === 0 && r.skipped === 0 && (
            <CountChip icon={SameIcon} n={0} label="no change" color={BRAND.inkFaint} />
          )}
        </Box>

        {hasDetail && (
          <ExpandIcon sx={{
            fontSize: 20, color: BRAND.inkFaint, flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .2s ease',
          }} />
        )}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.75, pt: .5 }}>
          {r.mode === 'insert-only' && (
            <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted, mb: 1.25 }}>
              Run as <strong>add new only</strong> — services already in the
              catalogue were left untouched, including any whose tariff differed.
            </Typography>
          )}

          {detailError && <Notice tone="danger">Could not load the detail: {detailError}</Notice>}

          {!detail && !detailError && (
            <Typography sx={{ fontSize: 12, color: BRAND.inkFaint }}>Loading detail…</Typography>
          )}

          {detail?.changes.length > 0 && (
            <>
              <SectionLabel sx={{ mb: .75 }}>
                What this release wrote
                {r.changesTruncated && ' (first 1,000)'}
              </SectionLabel>
              <Box sx={{
                display: 'grid', gap: .5, maxHeight: 300, overflowY: 'auto',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' },
                p: 1, borderRadius: '9px', background: BRAND.canvas,
              }}>
                {detail.changes.map((c, i) => (
                  <Box key={`${c.code}-${i}`} sx={{
                    display: 'flex', alignItems: 'baseline', gap: .75,
                    fontSize: 12, minWidth: 0,
                  }}>
                    <Box component="span" sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontWeight: 700, color: BRAND.ink, flexShrink: 0,
                    }}>
                      {c.code}
                    </Box>
                    <Box component="span" sx={{
                      color: BRAND.inkMuted, minWidth: 0, flex: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {c.service}
                    </Box>
                    {/* A service's right-hand figure is its tariff. A diagnosis has
                        no price of its own, so what belongs there is the G-DRG it
                        was filed under. A procedure's is how many ICD-10
                        indications folded into it — the only trace those codes
                        leave, since procedure_tarf has no column for them. */}
                    {c.indicationCount !== undefined ? (
                      <Box component="span" sx={{
                        fontSize: 11, color: BRAND.inkFaint, flexShrink: 0, whiteSpace: 'nowrap',
                      }} title={c.indications || undefined}>
                        {c.level ? `L${c.level} · ` : ''}
                        {c.indicationCount} ICD-10
                      </Box>
                    ) : c.core !== undefined ? (
                      <Box component="span" sx={{
                        fontFamily: MONO, fontWeight: 700, color: BRAND.ink, flexShrink: 0,
                      }}>
                        {c.core}
                        {c.level ? (
                          <Box component="span" sx={{ color: BRAND.inkFaint, fontWeight: 500 }}>
                            {' '}L{c.level}
                          </Box>
                        ) : null}
                      </Box>
                    ) : (
                      <Box component="span" sx={{
                        fontWeight: 700, color: BRAND.ink, flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {Number(c.amo).toFixed(2)}
                      </Box>
                    )}
                    {c.was !== null && c.was !== undefined && (
                      <Box component="span" sx={{ color: BRAND.inkFaint, flexShrink: 0, fontSize: 11 }}>
                        was {c.core !== undefined ? c.was : Number(c.was).toFixed(2)}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
              {r.changesTruncated && (
                <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted, mt: .75 }}>
                  This release changed more rows than the record keeps in detail.
                  The counts above cover all of them.
                </Typography>
              )}
            </>
          )}

          {detail?.skipped.length > 0 && (
            <Box sx={{ mt: detail.changes.length ? 2 : 0 }}>
              <SectionLabel sx={{ mb: .75 }}>Rows this release could not read</SectionLabel>
              <Box sx={{
                display: 'grid', gap: .5, maxHeight: 220, overflowY: 'auto',
                p: 1, borderRadius: '9px', background: '#FBEAEA',
              }}>
                {detail.skipped.map((s, i) => (
                  <Typography key={`${s.rowNumber}-${i}`} sx={{ fontSize: 11.5, color: '#8E2C2C' }}>
                    Row {s.rowNumber}
                    {s.code ? ` · ${s.code}` : ''}
                    {s.service ? ` · ${s.service}` : ''}
                    {' — '}{(s.errors || []).join('; ')}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function CountChip({ icon: Icon, n, label, color }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: .375,
      px: .75, py: .25, borderRadius: '6px',
      background: `${color}14`, border: `1px solid ${color}3D`,
      fontSize: 11, fontWeight: 700, color: BRAND.ink, whiteSpace: 'nowrap',
    }}>
      <Icon sx={{ fontSize: 13, color }} />
      {n} {label}
    </Box>
  );
}

// ── Small shared bits ───────────────────────────────────────────────────────
function FieldLabel({ children }) {
  return (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 800, letterSpacing: '.7px', textTransform: 'uppercase',
      color: BRAND.inkFaint, mb: .625,
    }}>
      {children}
    </Typography>
  );
}

function Hint({ children }) {
  return (
    <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted, mt: .625, lineHeight: 1.55 }}>
      {children}
    </Typography>
  );
}

function SectionLabel({ children, sx }) {
  return (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 800, letterSpacing: '.8px', textTransform: 'uppercase',
      color: BRAND.inkFaint, mb: 1, ...sx,
    }}>
      {children}
    </Typography>
  );
}

function MiniChip({ label, strong }) {
  return (
    <Box component="span" sx={{
      px: .875, py: .25, borderRadius: '6px', fontSize: 11,
      fontWeight: strong ? 800 : 600,
      background: strong ? BRAND.navySoft : BRAND.surface,
      color: strong ? BRAND.navy : BRAND.inkMuted,
      border: `1px solid ${strong ? 'transparent' : BRAND.line}`,
    }}>
      {label}
    </Box>
  );
}

function Notice({ tone, children }) {
  const map = {
    warning: { fg: '#8A5510', bg: '#FDF6EC', bd: `${BRAND.warning}55`, Icon: WarnIcon },
    danger:  { fg: '#8E2C2C', bg: '#FBEAEA', bd: `${BRAND.danger}55`,  Icon: ErrorIcon },
    // Not a problem — a fact about the sheet the operator needs before committing.
    info:    { fg: '#1B4E7A', bg: '#EAF2FA', bd: `${BRAND.info}55`,    Icon: InfoIcon },
  };
  const t = map[tone] || map.warning;
  const Icon = t.Icon;
  return (
    <Box sx={{
      display: 'flex', gap: 1, alignItems: 'flex-start',
      p: 1.5, mb: 1.25, borderRadius: '10px',
      background: t.bg, border: `1px solid ${t.bd}`,
    }}>
      <Icon sx={{ fontSize: 17, color: t.fg, flexShrink: 0, mt: '1px' }} />
      <Typography sx={{ fontSize: 12.5, color: t.fg, lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
}
